import { useState, useEffect, useCallback } from "react"
import { Modal, Tabs, Table, Select, Button, message, Tag, Typography, Space, Spin } from "antd"
import { swimminglyApi, gen, RoleHelper } from "./utils"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"

interface IDivision {
  divisionId: number
  divisionNumber: number
  divisionName: string
  clubIds: number[]
}

interface IClubScore {
  clubId: number
  clubName: string
  clubNameLong: string | null
  totalPoints: number
  place: number
}

interface IScoreDivision {
  divisionId: number | null
  divisionNumber: number | null
  divisionName: string
  clubs: IClubScore[]
}

interface IParticipatingClub {
  clubId: number
  clubName: string
  clubNameLong?: string | null
}

interface Props {
  visible: boolean
  onClose: () => void
  multiSessionMeetId: number
  championshipName: string
  participatingClubs: IParticipatingClub[]
}

export default function ChampionshipStandingsModal({
  visible,
  onClose,
  multiSessionMeetId,
  championshipName,
  participatingClubs,
}: Props) {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const isAdmin = new RoleHelper(user?.role).isAnyAdmin()

  const [divisions, setDivisions] = useState<IDivision[]>([])
  const [scoreDivisions, setScoreDivisions] = useState<IScoreDivision[]>([])
  const [assignments, setAssignments] = useState<Record<number, number | null>>({}) // clubId → divisionId
  const [loadingDivisions, setLoadingDivisions] = useState(false)
  const [loadingScores, setLoadingScores] = useState(false)
  const [savingDivisions, setSavingDivisions] = useState(false)
  const [activeTab, setActiveTab] = useState<string>("standings")

  const loadDivisions = useCallback(async () => {
    if (!multiSessionMeetId) return
    setLoadingDivisions(true)
    try {
      const data = await swimminglyApi
        .get(gen(`/api/getChampionshipDivisions/${multiSessionMeetId}`))
      if (data.status === "success" && Array.isArray(data.divisions)) {
        setDivisions(data.divisions)
        // Build initial assignment map from existing data
        const newAssignments: Record<number, number | null> = {}
        for (const div of data.divisions) {
          for (const cid of div.clubIds) {
            newAssignments[cid] = div.divisionId
          }
        }
        // Fill in clubs not yet assigned
        for (const club of participatingClubs) {
          if (club.clubId && newAssignments[club.clubId] === undefined) {
            newAssignments[club.clubId] = null
          }
        }
        setAssignments(newAssignments)
      }
    } catch (err) {
      message.error("Failed to load divisions")
    } finally {
      setLoadingDivisions(false)
    }
  }, [multiSessionMeetId, participatingClubs])

  const loadScores = useCallback(async () => {
    if (!multiSessionMeetId) return
    setLoadingScores(true)
    try {
      const data = await swimminglyApi
        .get(gen(`/api/getChampionshipScores/${multiSessionMeetId}`))
      if (data.status === "success" && Array.isArray(data.divisions)) {
        setScoreDivisions(data.divisions)
      }
    } catch (err) {
      message.error("Failed to load standings")
    } finally {
      setLoadingScores(false)
    }
  }, [multiSessionMeetId])

  useEffect(() => {
    if (visible && multiSessionMeetId) {
      loadDivisions()
      loadScores()
    }
  }, [visible, multiSessionMeetId, loadDivisions, loadScores])

  const saveDivisions = async () => {
    setSavingDivisions(true)
    try {
      const assignmentList = Object.entries(assignments)
        .filter(([, divId]) => divId != null)
        .map(([clubIdStr, divId]) => ({ clubId: Number(clubIdStr), divisionId: divId }))

      const data = await swimminglyApi
        .post(gen("/api/setClubDivisions"))
        .body({ multiSessionMeetId, assignments: assignmentList })

      if (data.status === "success") {
        message.success("Division assignments saved!")
        await loadScores()
      } else {
        message.error(data.message || "Failed to save divisions")
      }
    } catch (err) {
      message.error("Failed to save divisions")
    } finally {
      setSavingDivisions(false)
    }
  }

  const scoreColumns = [
    { title: "Place", dataIndex: "place", key: "place", width: 60, render: (p: number) => <strong>#{p}</strong> },
    {
      title: "Team",
      dataIndex: "clubName",
      key: "clubName",
      render: (name: string, row: IClubScore) => (
        <span>{row.clubNameLong || name}</span>
      ),
    },
    {
      title: "Points",
      dataIndex: "totalPoints",
      key: "totalPoints",
      width: 90,
      align: "right" as const,
      render: (pts: number) => <strong>{pts.toFixed(pts % 1 === 0 ? 0 : 2)}</strong>,
    },
  ]

  const standingsTab = (
    <Spin spinning={loadingScores}>
      {scoreDivisions.length === 0 && !loadingScores && (
        <Typography.Text type="secondary" style={{ display: "block", textAlign: "center", padding: "24px 0" }}>
          No scored results yet. Make sure both sessions have published results.
        </Typography.Text>
      )}
      {scoreDivisions.map((div) => (
        <div key={div.divisionId ?? "unassigned"} style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Tag color={div.divisionId ? "purple" : "default"}>
              {div.divisionId ? `Division ${div.divisionNumber}` : "Unassigned"}
            </Tag>
            <Typography.Text strong style={{ fontSize: 15 }}>
              {div.divisionName}
            </Typography.Text>
          </div>
          <Table
            size="small"
            dataSource={div.clubs}
            columns={scoreColumns}
            rowKey="clubId"
            pagination={false}
            bordered
          />
        </div>
      ))}
    </Spin>
  )

  const divisionsTab = isAdmin ? (
    <Spin spinning={loadingDivisions}>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Assign each participating club to a division. Clubs in the same division compete against each other.
      </Typography.Text>
      {divisions.length === 0 && !loadingDivisions && (
        <Typography.Text type="warning">
          No divisions have been configured for this championship. Divisions can be added during meet creation.
        </Typography.Text>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 500 }}>Club</th>
            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 500 }}>Division</th>
          </tr>
        </thead>
        <tbody>
          {participatingClubs.map((club) => (
            <tr key={club.clubId} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <td style={{ padding: "6px 8px" }}>{club.clubNameLong || club.clubName}</td>
              <td style={{ padding: "6px 8px" }}>
                <Select
                  size="small"
                  style={{ width: "100%" }}
                  placeholder="Select division…"
                  allowClear
                  value={assignments[club.clubId] ?? undefined}
                  onChange={(val) =>
                    setAssignments((prev) => ({ ...prev, [club.clubId]: val ?? null }))
                  }
                >
                  {divisions.map((div) => (
                    <Select.Option key={div.divisionId} value={div.divisionId}>
                      {div.divisionName}
                    </Select.Option>
                  ))}
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" loading={savingDivisions} onClick={saveDivisions}>
          Save Division Assignments
        </Button>
      </div>
    </Spin>
  ) : null

  const tabItems = [
    { key: "standings", label: "Standings", children: standingsTab },
    ...(isAdmin && divisions.length > 0
      ? [{ key: "divisions", label: "Assign Divisions", children: divisionsTab }]
      : []),
  ]

  return (
    <Modal
      open={visible}
      title={
        <Space>
          <Tag color="purple">Championship</Tag>
          <span>{championshipName} — Standings</span>
        </Space>
      }
      width={640}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </Modal>
  )
}
